import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('pn_token');
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      // Token was present but invalid/expired → tell the user, force re-login.
      const status = e?.response?.status;
      localStorage.removeItem('pn_token');
      setUser(null);
      if (status === 401) {
        try {
          toast.error('Sitzung abgelaufen – bitte neu anmelden. / Session expired – please sign in again.');
        } catch (_) { /* toast may not be mounted yet on cold boot */ }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('pn_token', data.access_token);
    await refresh();
    return data;
  }, [refresh]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('pn_token', data.access_token);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(() => {
    localStorage.removeItem('pn_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
